import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';

import { BrowserRouter } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorState } from './components/ErrorState';
import { FirebaseAuthProvider } from './auth/FirebaseAuthContext';
// Initialize the Firebase app/auth singletons (and surface config errors early).
import './auth/firebase';
import { installAuthRecovery } from './auth/installAuthRecovery';
import { installTranslationGuard } from './utils/installTranslationGuard';
import { captureFunnelEvent } from './services/telemetry';

// Register before React mounts so no request can go out unprotected. Not in an
// `App` effect, which would run a tick after the first render's fetches.
installAuthRecovery();

// Before React mounts: browser page translation otherwise crashes React's
// DOM updates with `NotFoundError` on removeChild/insertBefore.
installTranslationGuard((method) => {
  captureFunnelEvent('page_translation_dom_skip', {
    method,
    lang: document.documentElement.lang,
  });
});

// "ResizeObserver loop completed with undelivered notifications" is a benign
// browser notification, not a real error: the spec fires it whenever an
// observer callback triggers layout changes that can't all be delivered in a
// single frame, so they're deferred to the next one (exactly what happens as
// the score overlays / responsive banner settle on load). It has no runtime
// effect. The dev-server overlay is filtered in webpack.dev.js, because that
// client registers its window listener first. This capture listener still
// stops any later page listeners from treating it as a crash.
const RESIZE_OBSERVER_BENIGN =
  /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/;
window.addEventListener(
  'error',
  (event) => {
    if (RESIZE_OBSERVER_BENIGN.test(event.message)) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  },
  true
);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
  {
    onUncaughtError: (error, errorInfo) => {
      console.warn('Uncaught error:', error, errorInfo.componentStack);
    },
    onCaughtError: () => {
      // do not log errors handled by error boundaries
    },
  }
);

root.render(
  <FirebaseAuthProvider>
    <BrowserRouter>
      <ErrorBoundary FallbackComponent={ErrorState}>
        <App />
      </ErrorBoundary>
    </BrowserRouter>
  </FirebaseAuthProvider>
);
